//src/components/common/sidebar/SidebarMenuItem.tsx
import { ChevronDown, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui";
import { cn } from "@/lib/utils";
import type { MenuItem, MenuItemLabel } from "@/types";

type SidebarMenuItemProps = {
    item: MenuItem;
    isCollapsed: boolean;
    currentPath: string;
    isExpanded: boolean;
    onExpand: (itemId: string) => void;
};

export function SidebarMenuItem({
    item,
    isCollapsed,
    currentPath,
    isExpanded,
    onExpand
}: SidebarMenuItemProps) {
    const hasSubmenu = item.subItems && item.subItems.length > 0;

    // Check if this item or any of its submenu items is active
    const isActive = hasSubmenu
        ? item.subItems?.some(subItem => {
            // Check if the subItem itself is active
            if (currentPath === subItem.path) return true;
            // Check if any of the subItem's children are active
            if (subItem.subItems) {
                return subItem.subItems.some(grandChild => currentPath === grandChild.path);
            }
            return false;
        })
        : currentPath === item.path;

    // Force expansion when a submenu item is active
    const shouldBeExpanded = isExpanded || (hasSubmenu && isActive);

    const toggleSubmenu = (e: React.MouseEvent) => {
        if (hasSubmenu) {
            e.preventDefault();
            onExpand(item.id);
        }
    };

    // Get the label based on the current language (defaulting to English for now)
    const getLabel = (label: MenuItemLabel) => {
        // TODO: Replace with actual language detection
        return label.en;
    };

    if (isCollapsed) {
        return (
            <div className="mb-1">
                <Button
                    variant={isActive ? "secondary" : "ghost"}
                    size="icon"
                    className={cn(
                        "w-full h-9",
                        isActive && "bg-secondary text-secondary-foreground"
                    )}
                    title={getLabel(item.label)}
                    onClick={hasSubmenu ? toggleSubmenu : undefined}
                    asChild={!hasSubmenu}
                >
                    {!hasSubmenu ? (
                        <Link to={item.path}>
                            <item.icon className="h-5 w-5" strokeWidth={1.5} />
                        </Link>
                    ) : (
                        <item.icon className="h-5 w-5" strokeWidth={1.5} />
                    )}
                </Button>
            </div>
        );
    }

    return (
        <div>
            <Button
                variant={isActive ? "secondary" : "ghost"}
                className={cn(
                    "w-full justify-start text-left",
                    isActive && "bg-primary text-primary-foreground hover:bg-primary/90"
                )}
                onClick={toggleSubmenu}
                asChild={!hasSubmenu}
            >
                {!hasSubmenu ? (
                    <Link to={item.path} className="flex items-center w-full">
                        <item.icon className="mr-3 h-5 w-5" strokeWidth={1.5} />
                        <span className="flex-1">{getLabel(item.label)}</span>
                    </Link>
                ) : (
                    <>
                        <item.icon className="mr-3 h-5 w-5" strokeWidth={1.5} />
                        <span className="flex-1">{getLabel(item.label)}</span>
                        {shouldBeExpanded ? (
                            <ChevronDown className="h-4 w-4 ml-1" strokeWidth={1.5} />
                        ) : (
                            <ChevronRight className="h-4 w-4 ml-1" strokeWidth={1.5} />
                        )}
                    </>
                )}
            </Button>

            {shouldBeExpanded && hasSubmenu && (
                <div className="mt-1 mb-1 space-y-2 pl-2">
                    {item.subItems?.map((subItem) => {
                        const isSubItemActive = currentPath === subItem.path ||
                            (subItem.subItems && subItem.subItems.some(grandChild => currentPath === grandChild.path));

                        return (
                            <Button
                                key={subItem.id}
                                variant={isSubItemActive ? "secondary" : "ghost"}
                                className={cn(
                                    "w-full justify-start",
                                    isSubItemActive && "bg-primary text-primary-foreground hover:bg-primary/90"
                                )}
                                asChild
                            >
                                <Link to={subItem.path}>
                                    <subItem.icon className="mr-3 h-4 w-4" strokeWidth={1.5} />
                                    <span>{getLabel(subItem.label)}</span>
                                </Link>
                            </Button>
                        );
                    })}
                </div>
            )}
        </div>
    );
} 